import { interpolationInTime } from './InterpolationInTime';
import {
    createCircleCompleteFunc,
    createCircleProgressFunc,
    extractHTMLRewardedAdElements,
    getHTMLRewardedAd,
} from './HTMLRewardedAd';
import { throttle, throttleAsync } from 'Utility/async';
import { IShowable } from 'Utility/SDK/Drivers/IShowable';
import { report } from 'report';
import { AdType } from 'Utility/Tracking/DataSet/Ad';

export function getCrossPromoRewardedVideoAsync(options: {
    /** Video URL. */
    source: string;
    /** Duration for showing the close button. */
    duration: number;
    /** The action that will be executed when clicking on the play button or on the video. */
    action: () => Promise<void>;
    /** Call-to-action button text. */
    cta: string;
}): IShowable {
    const root = getHTMLRewardedAd();
    const { video, btnPlay, btnClose, overlay } = extractHTMLRewardedAdElements(root);

    return {
        loadAsync: async () => {
            return new Promise<void>((resolve, reject) => {
                const successFunc = () => {
                    removeListenersFunc();
                    resolve();
                };
                const failFunc = () => {
                    removeListenersFunc();
                    reject('fail to load video');
                };
                const removeListenersFunc = () => {
                    video.removeEventListener('canplaythrough', successFunc, true);
                    video.removeEventListener('error', failFunc, true);
                };

                // Update play button label.
                btnPlay.innerHTML = options.cta;

                video.addEventListener('canplaythrough', successFunc, true);
                video.addEventListener('error', failFunc, true);
                video.src = options.source;
            });
        },
        showAsync: async () => {
            return new Promise<void>(resolve => {
                document.body.appendChild(root);

                // Circle time progression.
                const progressUpdateFunc = createCircleProgressFunc(root);
                const progressCompleteFunc = createCircleCompleteFunc(root);
                const lerp = interpolationInTime(options.duration, progressUpdateFunc, progressCompleteFunc);

                const closeFunc = () => {
                    lerp.stop();
                    root.remove();

                    // Remove event listeners.
                    video.removeEventListener('error', closeFunc);
                    video.removeEventListener('click', playFunc, true);
                    btnPlay.removeEventListener('click', playFunc, true);
                    btnClose.removeEventListener('click', closeFunc, true);

                    resolve();
                };

                // Throttling play function so only the first click is executed immediately.
                const playFunc = throttleAsync(() => {
                    return new Promise<void>(resolvePlay => {
                        video.pause();
                        overlay.classList.replace('fade-out', 'fade-in');

                        const play = () => {
                            const playPromise = video.play();

                            if (playPromise !== undefined) {
                                playPromise
                                    .then(() => {
                                        console.log('xPromoPlayThen');
                                    })
                                    .catch(error => {
                                        console.error('xPromoPlayCatch', error);
                                        report('Failed to display XPromo ad instance.', {
                                            error,
                                            once: true,
                                            sampleRate: 0.01,
                                            fingerprint: 'CrossPromoRewardedVideo:playFunc',
                                        });
                                    })
                                    .finally(() => {
                                        overlay.classList.replace('fade-in', 'fade-out');
                                        resolvePlay();
                                    });
                            }
                        };

                        options
                            .action()
                            .catch(e => {
                                console.warn(e);
                            })
                            .finally(play);
                    });
                }, 1500);

                video.play().then(() => {
                    // Add event listeners.
                    video.addEventListener('error', closeFunc);
                    video.addEventListener('click', playFunc, true);
                    btnPlay.addEventListener('click', playFunc, true);
                    btnClose.addEventListener('click', closeFunc, true);
                });
            });
        },
    };
}
